'use client';

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import MinusIcon from '@/fo/icons/base/icons/minus.svg';
import PlusIcon from '@/fo/icons/base/icons/plus.svg';
import { UiKitButtonBase, ButtonColor } from '@/fo/ui/kit/button/base';
import { UiKitCollapseBase } from '@/fo/ui/kit/collapse/base';
import { UiModScrollDraggable } from '@/fo/ui/mod/scroll/draggable';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { styles } from './UiKitPanelBase.styles';

type Variants = VariantProps<typeof styles>;

export interface UiKitPanelBaseProps {
  activeIndex?: number;
  className?: string;
  tabsClassName?: string;
  tabClassName?: string;
  contentClassName?: string;
  tabsWrapClassName?: string;
  tabActiveColor?: ButtonColor;
  headerTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  view?: Variants['view'];
  items: {
    header: React.ReactNode;
    content: React.ReactNode;
    disabled?: boolean;
  }[];
  onTabChange?: (index: number) => void;
}

export function UiKitPanelBase({
  activeIndex: activeIndexProp = 0,
  view = 'auto',
  items,
  className,
  tabsClassName,
  tabClassName,
  contentClassName,
  tabsWrapClassName,
  tabActiveColor,
  headerTag: HeaderTag,
  onTabChange
}: UiKitPanelBaseProps) {
  const [activeIndex, setActiveIndex] = useState(activeIndexProp);
  const headerRefs = useRef<HTMLDivElement[]>([]);
  const contentRefs = useRef<HTMLDivElement[]>([]);
  const slots = styles({ view });
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);

  const setHeaderRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      if (element) {
        headerRefs.current[index] = element;
      }
    },
    []
  );

  const setContentRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      if (element) {
        contentRefs.current[index] = element;
      }
    },
    []
  );

  useEffect(() => {
    setActiveIndex(activeIndexProp);
  }, [activeIndexProp]);

  const handleClick = (index: number) => () => {
    setActiveIndex(index);
    onTabChange?.(index);

    if (view === 'auto') {
      scrollTargetRef.current = contentRefs.current[index];
    }
  };
  const wrapHeader = (header: React.ReactNode) => {
    if (!HeaderTag) {
      return header;
    }

    return <HeaderTag className="m-0">{header}</HeaderTag>;
  };

  return (
    <div className={slots.base({ className })}>
      <UiModScrollDraggable
        className={slots.tabs({ className: tabsClassName })}
      >
        <div className={slots.tabsWrap({ className: tabsWrapClassName })}>
          {items.map((item, index) => (
            <Fragment key={index}>
              {view !== 'additionalTab' &&
                wrapHeader(
                  <UiKitButtonBase
                    color={
                      activeIndex === index
                        ? (tabActiveColor ?? 'default')
                        : 'ghost'
                    }
                    rounded="full"
                    onClick={handleClick(index)}
                    disabled={item.disabled}
                    className={
                      item.disabled
                        ? `${tabClassName} opacity-60`
                        : tabClassName
                    }
                  >
                    {item.header}
                  </UiKitButtonBase>
                )}
              {view === 'additionalTab' &&
                wrapHeader(
                  <button
                    type="button"
                    className={slots.additionalTab({
                      active: activeIndex === index
                    })}
                    onClick={handleClick(index)}
                  >
                    {item.header}
                  </button>
                )}
            </Fragment>
          ))}
        </div>
      </UiModScrollDraggable>

      {items.map((item, index) => (
        <Fragment key={index}>
          <div
            ref={setHeaderRef(index)}
            className={slots.header({
              active: activeIndex === index
            })}
            onClick={() => {
              setActiveIndex(index);

              if (view === 'auto') {
                scrollTargetRef.current = headerRefs.current[index];
              }
            }}
          >
            {wrapHeader(item.header)}
            <span className={slots.headerIcon()}>
              {activeIndex === index && <MinusIcon />}
              {activeIndex !== index && <PlusIcon />}
            </span>
          </div>
          <UiKitCollapseBase
            className={slots.wrapper({
              className: contentClassName,
              active: activeIndex === index
            })}
            collapse={activeIndex !== index}
            onTransitionEnd={() => {
              if (scrollTargetRef.current) {
                utilScrollToElement(scrollTargetRef.current);
                scrollTargetRef.current = null;
              }
            }}
          >
            <div
              ref={setContentRef(index)}
              className={slots.content({
                active: activeIndex === index
              })}
            >
              {item.content}
            </div>
          </UiKitCollapseBase>
        </Fragment>
      ))}
    </div>
  );
}
